import React, { useState } from "react";

const MonthlyTotalsTable = ({ selectedBusiness, businessData, excludedCategories = [], onShowDetails }) => {
	const [expandedCategories, setExpandedCategories] = useState({});
	const [allExpanded, setAllExpanded] = useState(false); // Track expand/collapse state

	if (!businessData || !businessData.categories) return null;

	const excludedSet = Array.isArray(excludedCategories) && excludedCategories.length > 0 ? new Set(excludedCategories.map((c) => String(c).trim().toLowerCase())) : null;
	const monthsDenominator = businessData?.monthsInRange || 12;
	const categories = excludedSet
		? businessData.categories
				.map((cat) => {
					const hasSubs = Array.isArray(cat.subcategories) && cat.subcategories.length > 0;
					if (hasSubs) {
						const filteredSubs = cat.subcategories.filter((sc) => !excludedSet.has(String(sc.category).trim().toLowerCase()));
						if (filteredSubs.length === 0) return null;
						const len = cat.monthlyTotals?.length || 12;
						const recomputedMonthlyTotals = Array(len).fill(0);
						filteredSubs.forEach((sc) => {
							sc.monthlyTotals.forEach((amt, idx) => {
								recomputedMonthlyTotals[idx] += amt;
							});
						});
						const total = recomputedMonthlyTotals.reduce((s, v) => s + v, 0);
						return {
							...cat,
							subcategories: filteredSubs,
							monthlyTotals: recomputedMonthlyTotals,
							total,
							monthlyAverage: total / monthsDenominator,
						};
					}
					if (excludedSet.has(String(cat.category).trim().toLowerCase())) return null;
					const total = Array.isArray(cat.monthlyTotals) ? cat.monthlyTotals.reduce((s, v) => s + v, 0) : cat.total || 0;
					return {
						...cat,
						total,
						monthlyAverage: total / monthsDenominator,
					};
				})
				.filter(Boolean)
		: businessData.categories;
	const toggleCategory = (categoryName) => {
		setExpandedCategories((prev) => ({
			...prev,
			[categoryName]: !prev[categoryName],
		}));
	};

	const handleExpandCollapseAll = () => {
		const newState = {};
		categories.forEach((cat) => {
			if (cat.subcategories && cat.subcategories.length > 0) {
				newState[cat.category] = !allExpanded;
			}
		});
		setExpandedCategories(newState);
		setAllExpanded(!allExpanded);
	};

	const sortedMonths = Array.from({ length: 12 }, (_, idx) => {
		const date = new Date(2024, idx);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const monthlyTotalsRow = Array(12).fill(0);

	categories.forEach((category) => {
		category.monthlyTotals.forEach((amount, idx) => {
			monthlyTotalsRow[idx] += amount;
		});
	});

	// Helper to retrieve all contributing entries for a category cell by month index
	const getEntriesForMonth = (category, monthIdx) => {
		const results = [];
		const pushIfMatch = (entry) => {
			if (!entry || !entry.date) return;
			const d = new Date(entry.date);
			if (d.getMonth() === monthIdx) results.push(entry);
		};
		if (Array.isArray(category.subcategories) && category.subcategories.length > 0) {
			category.subcategories.forEach((sc) => {
				if (Array.isArray(sc.entries)) sc.entries.forEach(pushIfMatch);
			});
		} else if (Array.isArray(category.entries)) {
			category.entries.forEach(pushIfMatch);
		}
		return results;
	};
	const getEntriesForFooterMonth = (monthIdx) => {
		const results = [];
		categories.forEach((cat) => {
			const arr = getEntriesForMonth(cat, monthIdx);
			if (arr.length) results.push(...arr);
		});
		return results;
	};

	const grandTotal = monthlyTotalsRow.reduce((sum, val) => sum + val, 0);
	const grandAverage = grandTotal / monthsDenominator;

	return (
		<div className="table two-axis">
			<div className="row hd">
				<div className="col spacing">
					{/* Expand/Collapse All Button */}
					<a className="l-btn" onClick={handleExpandCollapseAll}>
						{allExpanded ? "-" : "+"}
					</a>
				</div>
				{sortedMonths.map((monthName, idx) => (
					<div className="col" key={idx}>
						{monthName}
					</div>
				))}
				<div className="col total">Total</div>
				<div className="col total">Avg</div>
			</div>

			{categories.map((category) => {
				const isExpanded = expandedCategories[category.category] || false;
				const hasSubcategories = category.subcategories && category.subcategories.length > 0;

				return (
					<React.Fragment key={category.category}>
						{/* Main Category Row */}
						<div className="row cat">
							<div className="col hd" onClick={() => hasSubcategories && toggleCategory(category.category)}>
								{category.category}
								{hasSubcategories && <span className="expand-toggle">{isExpanded ? "−" : "+"}</span>}
							</div>
							{category.monthlyTotals.map((val, idx) => (
								<div
									onClick={() => {
										if (onShowDetails)
											onShowDetails({
												entries: getEntriesForMonth(category, idx),
												title: `${category.category} | ${sortedMonths[idx]}`,
											});
									}}
									className={val !== 0 ? "col" : "col zero"}
									key={idx}>
									<span>{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</span>
								</div>
							))}
							<div className="col total">{Math.round(category.total).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
							<div className="col total">{Math.round(category.monthlyAverage).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
						</div>

						{/* Subcategories */}
						{hasSubcategories &&
							category.subcategories.map((subcat) => (
								<div key={subcat.category} className={`row sub ${isExpanded ? "expanded" : "collapsed"}`}>
									<div className="col hd">{subcat.category}</div>
									{subcat.monthlyTotals.map((val, idx) => (
										<div
											className={val !== 0 ? "col" : "col zero"}
											key={idx}
											onClick={() => {
												if (onShowDetails)
													onShowDetails({
														entries: getEntriesForMonth(subcat, idx),
														title: `${subcat.category} | ${sortedMonths[idx]}`,
													});
											}}>
											<span>{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</span>
										</div>
									))}
									<div className="col total">{Math.round(subcat.total).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
									<div className="col total">{Math.round(subcat.monthlyAverage).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
								</div>
							))}
					</React.Fragment>
				);
			})}

			{/* Footer Row */}
			<div className="row footer-row">
				<div className="col hd">Monthly Totals</div>
				{monthlyTotalsRow.map((val, idx) => (
					<div
						className="col total"
						key={idx}
						onClick={() => {
							if (onShowDetails)
								onShowDetails({
									entries: getEntriesForFooterMonth(idx),
									title: `Monthly Totals | ${sortedMonths[idx]}`,
								});
						}}>
						{Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}
					</div>
				))}
				<div className="col total">{Math.round(grandTotal).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
				<div className="col total">{Math.round(grandAverage).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 })}</div>
			</div>
		</div>
	);
};

export default MonthlyTotalsTable;
